import { productEnabled } from '@get-bridge/learn-legacy-shared/dist/utils/accountConfig';
import isFeatureEnabled from '@get-bridge/learn-legacy-shared/dist/utils/isFeatureEnabled';
import { isRootAccount } from '@get-bridge/learn-legacy-shared/dist/utils/accountConfig';
import me from './primaryLinks/me';
import team from './primaryLinks/team';
import company from './primaryLinks/company';
import insights from './primaryLinks/insights';
import author from './primaryLinks/author';
import admin from './primaryLinks/admin';
import analytics from './primaryLinks/analytics';
import {
  canAdminViewAnalytics,
  canViewAnalytics,
} from '@get-bridge/learn-legacy-shared/dist/utils/canViewPermissions';

/*
  This util constructs the list of links to pass into the AtlasDummy component.
  It is responsible for defining the correct routes for each link,
    and defining the correct access rules for each link.

  TODO:
  When all of Learn is behind the JS Runtime,
    we should delete all this and instead use link configuration from bridge-nav's platform manifest.

  Right now all this logic is duplicated (once here, once in bridge-nav's manifest).
  Long-term, it should only be defined in the manifest file.

  Until Learn is behind the runtime, this needs to be kept consistent with bridge-nav's manifest.
  Any link changes, additions, or removals need to happen both here, and in bridge-nav/bridge-platform-runtime.yml
*/

const isCareerEnabled = () => productEnabled('career');
const isPerformEnabled = () => productEnabled('perform');
const isConnectEnabled = () => productEnabled('connect');
const isLearnOnly = () =>
  !isCareerEnabled() && !isPerformEnabled() && !isConnectEnabled();

export default ({ hosts, user, goalTerminology }) => {
  const buildLinkGroups = (...groups) =>
    groups
      .filter(Boolean)
      .map(arr => arr.filter(Boolean))
      .filter(group => group.length > 0)
      .map((links, i) => ({
        key: `group-${i + 1}`,
        links: isFeatureEnabled('perform_goals_okr_in_nav')
          ? links.map(prepareLink)
          : links,
      }));

  // Use OKR name if present *and* OKR terminology is enabled
  const prepareLink = (link) => {
    if ('sections' in link) {
      link.sections
        .filter(section => 'okrName' in section)
        .forEach(section => {
          if (goalTerminology === 'OKR') section.name = section.okrName
          delete section.okrName
        });
    }

    if (!('okrName' in link)) return link;

    const modifiedLink = goalTerminology === 'OKR'
      ? { ...link, name: link.okrName }
      : { ...link }
    delete modifiedLink.okrName
    return modifiedLink
  }

  const getFirstHref = linkGroups => {
    if (linkGroups) {
      for (let i = 0; i < linkGroups.length; i += 1) {
        const linkGroup = linkGroups[i];
        if (linkGroup.links) {
          for (let j = 0; j < linkGroup.links.length; j += 1) {
            const link = linkGroup.links[j];
            if (link.href) return { href: link.href, host: link.host };
            if (link.sections) {
              for (let k = 0; k < link.sections.length; k += 1) {
                const section = link.sections[k];
                if (section.href)
                  return { href: section.href, host: section.host };
              }
            }
          }
        }
      }
    }
    return {};
  };

  function buildPrimaryLink(obj, linkGroups) {
    if (!linkGroups || !linkGroups.length) return null;

    const { href, host } = getFirstHref(linkGroups);
    return {
      ...obj,
      host,
      href,
      linkGroups,
    };
  }

  const primaryMe = myLearningOnRuntime =>
    buildPrimaryLink(
      {
        id: 'PRIMARY_ME',
        name: I18n.t('Me'),
        isUserAvatar: true,
      },
      buildLinkGroups(
        [
          me.today(hosts),
          me.skillPlan(hosts),
          me.careerVision(hosts),
          me.history(hosts, user),
        ],
        [
          me.goals(hosts, user),
          me.feedback(hosts, user),
          me.learning(hosts, myLearningOnRuntime),
          me.journeys(hosts),
        ],
        [me.profile(hosts, user)],
        [me.tools(hosts)],
      ),
    );

  const primaryTeam = () =>
    isPerformEnabled() &&
    buildPrimaryLink(
      {
        id: 'PRIMARY_TEAM',
        name: I18n.t('Team'),
        logo: 'team',
      },
      hosts.perform
        ? buildLinkGroups([
            team.oneOnOneAgendas(hosts),
            team.groupAgendas(hosts),
            team.goals(hosts, user),
            team.skills(hosts, user),
            team.engagementSurveys(hosts, user),
          ])
        : [],
    );

  const primaryCompany = () =>
    buildPrimaryLink(
      {
        id: 'PRIMARY_COMPANY',
        name: I18n.t('Company'),
        logo: 'people',
      },
      buildLinkGroups([
        company.peopleSearch(hosts),
        company.orgChart(hosts, user),
        company.goals(hosts),
      ]),
    );

  const primaryInsights = insightsOnRuntime => {
    const insightsLink = {
      id: 'PRIMARY_INSIGHTS',
      name: I18n.t('Insights'),
      logo: 'insights',
    };
    const secondaryLinks = buildLinkGroups(
      insights(hosts, user, insightsOnRuntime),
    );
    if (
      secondaryLinks.length === 1 &&
      secondaryLinks[0].links.length === 1 &&
      secondaryLinks[0].links[0].id === 'SECONDARY_INSIGHTS_LEARNING'
    ) {
      if (insightsOnRuntime && !hosts.perform) return null;
      if (!insightsOnRuntime && !hosts.learn) return null;
      if (isFeatureEnabled('learn_insights_retirement')) return null;
      // this rule was added for BRD-325, Insights should not show a secondary nav if "Learning" is the only option.
      // Learn-only customers won't see any of the other insights options, and a secondary nav with one link was deemed to take up too much space.
      return {
        ...insightsLink,
        href: '/insights',
        otherHrefs: ['/insights/.*'],
        host: insightsOnRuntime ? hosts.perform : hosts.learn,
        linkGroups: [],
      };
    }

    return buildPrimaryLink(insightsLink, secondaryLinks);
  };

  const primaryAnalytics = () =>
    hosts.perform &&
    canViewAnalytics(user) && {
      id: 'PRIMARY_ANALYTICS',
      name: I18n.t('Analytics'),
      logo: 'analytics',
      host: hosts.perform,
      linkGroups: buildLinkGroups([
        analytics.overview(hosts),
        analytics.learners(hosts),
        isFeatureEnabled('datareport_journeys_dashboard')
          && analytics.journeys(hosts),
        canAdminViewAnalytics(user.roles) && analytics.usageMetrics(hosts),
        isFeatureEnabled('datareport_perform_goal_extract')
          && isPerformEnabled()
          && canAdminViewAnalytics(user.roles)
          && isRootAccount()
          && analytics.goals(hosts),
        isFeatureEnabled('datareport_leaderboard') && analytics.leaderboard(hosts),
      ]),
    };

  const primaryAuthor = (authorOnRuntime) =>
    buildPrimaryLink(
      {
        id: 'PRIMARY_AUTHOR',
        name: I18n.t('Author'),
        logo: 'author',
      },
      hosts.learn
        ? buildLinkGroups([
            author.courses(hosts, authorOnRuntime),
            author.programs(hosts, authorOnRuntime),
            author.liveTrainings(hosts, authorOnRuntime),
            author.surveys(hosts, authorOnRuntime),
            author.checkpoints(hosts, authorOnRuntime),
            author.journeys(hosts, authorOnRuntime),
          ])
        : [],
    );

  const primaryAdmin = (adminOnRuntime) => {
    const linkGroups = [
      admin.general(hosts, adminOnRuntime),
      admin.usersAndPermissions(hosts, adminOnRuntime),
      admin.performance(hosts, user),
      admin.learning(hosts, adminOnRuntime),
      admin.engagement(hosts),
      admin.tools(hosts, adminOnRuntime),
    ].filter(l => (!!l && l.sections.length > 0) || l.quickActions.length > 0);

    return buildPrimaryLink(
      {
        id: 'PRIMARY_ADMIN',
        name: I18n.t('Admin'),
        logo: 'admin',
      },
      buildLinkGroups(linkGroups),
    );
  };

  const primaryLearnOnly = myLearningOnRuntime => {
    if (myLearningOnRuntime && !hosts.perform) return null;
    if (!myLearningOnRuntime && !hosts.learn) return null;

    const isJourneysEnabled =
      isFeatureEnabled('journeys_mvp') &&
      (isRootAccount() || isFeatureEnabled('journeys_subaccount_support'));
    return {
      id: 'PRIMARY_LEARN_ONLY',
      name: I18n.t('Learn'),
      href: '/learner/courses',
      otherHrefs: [
        '/learner/library',
        '/learner/calendar',
        '/learner/category/.*',
        '/learner/approver/approvals',
        '/learner/training/.*/enroll',
      ],
      logo: 'learning',
      host: myLearningOnRuntime ? hosts.perform : hosts.learn,
      linkGroups: isJourneysEnabled
        ? buildLinkGroups([
            me.learning(hosts, myLearningOnRuntime),
            me.journeys(hosts),
          ])
        : [],
    };
  };

  const primaryLearnOnlyTools = () =>
    hosts.learn && {
      id: 'PRIMARY_LEARN_ONLY_TOOLS',
      name: I18n.t('Tools'),
      logo: 'tools',
      linkGroups: [
        {
          key: 'group-1',
          links: [
            {
              id: 'LTI_TOOLS_PLACEHOLDER_LEARNER_NAV',
              href: '#',
              name: '',
            },
          ],
        },
      ],
    };

  const buildPrimaryLinks = () => {
    const myLearningOnRuntime = isFeatureEnabled(
      'learn_my_learning_on_runtime',
    );
    const insightsOnRuntime = isFeatureEnabled('learn_insights_on_runtime');
    const authorOnRuntime = isFeatureEnabled('learn_author_on_runtime');
    const adminOnRuntime = isFeatureEnabled('learn_admin_on_runtime');
    if (isLearnOnly()) {
      return [
        primaryLearnOnly(myLearningOnRuntime),
        primaryLearnOnlyTools(),
        primaryInsights(insightsOnRuntime),
        primaryAnalytics(),
        primaryAuthor(authorOnRuntime),
        primaryAdmin(adminOnRuntime),
      ];
    }

    return [
      primaryMe(myLearningOnRuntime),
      primaryTeam(),
      primaryCompany(),
      primaryInsights(insightsOnRuntime),
      primaryAnalytics(),
      primaryAuthor(authorOnRuntime),
      primaryAdmin(adminOnRuntime),
    ];
  };

  return buildPrimaryLinks().filter(Boolean);
};



// WEBPACK FOOTER //
// ./packages/bridge-global-nav/src/utils/buildAtlasLinks/index.js